import { useMutation } from '@apollo/client'
import { useNotifications } from '@joor/design-system'

import {
  BulkDeleteRetailerLocationsInput,
  BulkDeleteRetailerUploadsInput,
  BulkUpsertRetailerLocationsInput,
  BulkUpsertRetailerUploadsInput,
  UpdateRetailerProfileInput,
} from '__generated__/atlas-types'

import { fromGlobalId } from 'utils/transformations/graphql'

import { atlasClient } from 'graphql/client'
import { atlas } from 'graphql/tags'

import { RetailerProfile } from '../retailerProfilePage.types'
import {
  checkBasicInfoChanges,
  checkLocationsChanges,
  getBasicInfoInput,
  getDeleteLocationsInput,
  getDeleteProfileImagesInput,
  getLocationsInput,
  getLocationsToDelete,
  getNewProfileImages,
  getProfileImagesInput,
  getProfileImagesToDelete,
} from './RetailerProfileEditMode.utils'

const UPDATE_RETAILER_BASIC_INFO = atlas`#graphql
   mutation UpdateRetailerProfile(
    $input: UpdateRetailerProfileInput!
  ) {
    updateRetailerProfile(input: $input) {
      id
    }
  }
`

export const useSaveBasicInfo = () => {
  const [mutate, { loading: savingBasicInfo }] = useMutation(
    UPDATE_RETAILER_BASIC_INFO,
    {
      client: atlasClient,
      notifyOnNetworkStatusChange: true,
    },
  )

  const saveBasicInfo = async (input: UpdateRetailerProfileInput) => {
    let success

    try {
      const response = await mutate({
        variables: {
          input,
        },
      })
      const retailerId = fromGlobalId(
        response?.data?.updateRetailerProfile?.id,
      )?.id
      success = Boolean(retailerId)
    } finally {
      return success
    }
  }

  return { saveBasicInfo, savingBasicInfo }
}

const UPDATE_RETAILER_PROFILE_IMAGES = atlas`#graphql
  mutation BulkUpsertRetailerUploads($input: BulkUpsertRetailerUploadsInput!) {
    bulkUpsertRetailerUploads(input: $input) {
    retailerUploadIds
    }
  }
`

const useSaveProfileImages = () => {
  const [mutate] = useMutation(UPDATE_RETAILER_PROFILE_IMAGES, {
    client: atlasClient,
    notifyOnNetworkStatusChange: true,
  })

  const saveProfileImages = async (input: BulkUpsertRetailerUploadsInput) => {
    let success
    try {
      const response = await mutate({
        variables: {
          input,
        },
      })
      const retailerUploadIds =
        response?.data?.bulkUpsertRetailerUploads?.retailerUploadIds

      success = Boolean(retailerUploadIds?.length)
    } finally {
      return success
    }
  }

  return { saveProfileImages }
}

const DELETE_RETAILER_PROFILE_IMAGES = atlas`#graphql
  mutation BulkDeleteRetailerUploads ($input: BulkDeleteRetailerUploadsInput!) {
  bulkDeleteRetailerUploads (input: $input) {
    retailerUploadIds
  }
}
`

const useDeleteProfileImages = () => {
  const [mutate] = useMutation(DELETE_RETAILER_PROFILE_IMAGES, {
    client: atlasClient,
  })

  const deleteProfileImages = async (input: BulkDeleteRetailerUploadsInput) => {
    let success
    try {
      const response = await mutate({
        variables: {
          input,
        },
      })
      const retailerUploadIds =
        response.data.bulkDeleteRetailerUploads.retailerUploadIds
      success = Boolean(retailerUploadIds?.length)
    } finally {
      return success
    }
  }

  return { deleteProfileImages }
}

const UPDATE_LOCATIONS = atlas`#graphql
  mutation BulkUpsertRetailerLocations($input: BulkUpsertRetailerLocationsInput!) {
    bulkUpsertRetailerLocations(input: $input) {
      locations {
        id
      }
    }
  }
`

const useSaveLocations = () => {
  const [mutate] = useMutation(UPDATE_LOCATIONS, {
    client: atlasClient,
    notifyOnNetworkStatusChange: true,
  })

  const saveLocations = async (input: BulkUpsertRetailerLocationsInput) => {
    let success
    try {
      const response = await mutate({
        variables: {
          input,
        },
      })
      const locationsToUpdate = input?.locations?.length
      const updatedLocations =
        response?.data?.bulkUpsertRetailerLocations?.locations?.length
      success = locationsToUpdate === updatedLocations
    } finally {
      return success
    }
  }

  return { saveLocations }
}

const DELETE_RETAILER_LOCATIONS = atlas`#graphql
  mutation BulkDeleteRetailerLocation ($input: BulkDeleteRetailerLocationsInput!) {
    bulkDeleteRetailerLocations (input: $input) {
      retailerLocationIds
    }
  }
`

const useDeleteLocations = () => {
  const [mutate] = useMutation(DELETE_RETAILER_LOCATIONS, {
    client: atlasClient,
  })

  const deleteLocations = async (input: BulkDeleteRetailerLocationsInput) => {
    let success
    try {
      const response = await mutate({
        variables: {
          input,
        },
      })
      const deletedLocationIds =
        response?.data?.bulkDeleteRetailerLocations?.retailerLocationIds

      success = deletedLocationIds.length
    } finally {
      return success
    }
  }

  return { deleteLocations }
}

export const useSaveProfile = () => {
  const { addToasts } = useNotifications()
  const { saveBasicInfo } = useSaveBasicInfo()
  const { saveProfileImages } = useSaveProfileImages()
  const { saveLocations } = useSaveLocations()
  const { deleteLocations } = useDeleteLocations()
  const { deleteProfileImages } = useDeleteProfileImages()

  const saveRetailerProfile = async ({
    previousData,
    updatedData,
  }: {
    previousData: RetailerProfile
    updatedData: RetailerProfile
  }) => {
    let mutationSuccess = []
    const shouldUpdateBasicInfo = checkBasicInfoChanges({
      previousData,
      updatedData,
    })
    const locationsToUpdate = checkLocationsChanges({
      previousLocations: previousData?.locations,
      updatedLocations: updatedData?.locations,
    })
    const locationsToDelete = getLocationsToDelete({
      previousLocations: previousData?.locations,
      updatedLocations: updatedData?.locations,
    })
    const profileImagesToDelete = getProfileImagesToDelete({
      previousData,
      updatedData,
    })
    const addedProfileImages = getNewProfileImages({
      previousData,
      updatedData,
    })
    try {
      if (shouldUpdateBasicInfo) {
        const success = await saveBasicInfo(getBasicInfoInput(updatedData))
        mutationSuccess.push(success)
      }
      if (Boolean(addedProfileImages.length)) {
        const success = await saveProfileImages(
          getProfileImagesInput({
            previousData,
            updatedData,
          }),
        )
        mutationSuccess.push(success)
      }
      if (Boolean(locationsToUpdate?.length)) {
        const success = await saveLocations(
          getLocationsInput(locationsToUpdate),
        )
        mutationSuccess.push(success)
      }
      if (Boolean(locationsToDelete?.length)) {
        const success = await deleteLocations(
          getDeleteLocationsInput(locationsToDelete),
        )
        mutationSuccess.push(success)
      }
      if (Boolean(profileImagesToDelete?.length)) {
        const success = await deleteProfileImages(
          getDeleteProfileImagesInput(profileImagesToDelete),
        )
        mutationSuccess.push(success)
      }
    } finally {
      if (mutationSuccess.some((success) => !success)) {
        addToasts([
          {
            type: 'error',
            title: 'Saving Error',
            description: 'There was an error updating your profile',
          },
        ])
      } else {
        addToasts([
          {
            type: 'success',
            title: 'Saving Success',
            description: 'Your profile was successfully updated',
          },
        ])
      }
    }
  }
  return { saveRetailerProfile }
}
